import React, { useEffect, useState } from "react";
import { Box, useTheme, CircularProgress, Typography } from "@mui/material";
import { tokens } from "../../theme";
import EmailIcon from "@mui/icons-material/Email";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import TrafficIcon from "@mui/icons-material/Traffic";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import EventNoteIcon from "@mui/icons-material/EventNote"; 
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"; 
import AccessTimeIcon from "@mui/icons-material/AccessTime"; 
import Header from "./Header";
import StatBox from "./StatBox";
import config from "../../Config";

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [userStats, setUserStats] = useState(null);
  const [orderStats, setOrderStats] = useState(null);
  const [walletStats, setWalletStats] = useState(null); 

  useEffect(() => {
    const fetchData = async () => {
      const token = sessionStorage.getItem("adminToken");

      if (!token) {
        console.error("No token found");
        return;
      }

      try {
        const response = await fetch(`${config.apiBaseUrl}/admin/adminDashboard`, {
          method: "GET",
          headers: {
            "x-access-token": token, 
          },
        });

        const data = await response.json();

        if (data) {
          setUserStats(data.userStata);
          setOrderStats(data.orderStat);
          setWalletStats(data.walletStat); 
        }
      } catch (error) {
        console.error("Error fetching data: ", error);
      }
    };

    fetchData();
  }, []);

  if (!userStats || !orderStats || !walletStats) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="DASHBOARD" subtitle="Welcome to your dashboard" />
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows="140px"
        gap="20px"
      >
        <Box
          gridColumn="span 3"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={userStats.totalUser}
            subtitle="Total Users"
            progress="0.75"
            increase="+14%"
            icon={
              <PersonAddIcon
                sx={{ color: colors.greenAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>
        <Box
          gridColumn="span 3"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={userStats.activeUser}
            subtitle="Active Users"
            progress="0.30"
            increase="+5%"
            icon={
              <EmailIcon
                sx={{ color: colors.greenAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>
        
        <Box
          gridColumn="span 3"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={orderStats.totalOrderPlace}
            subtitle="Total Orders Placed"
            progress="0.50"
            increase="+21%"
            icon={
              <PointOfSaleIcon
                sx={{ color: colors.greenAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>
        <Box
          gridColumn="span 3"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={orderStats.totalHoldOrders}
            subtitle="Orders on Hold"
            progress="0.80"
            increase="+43%"
            icon={
              <TrafficIcon
                sx={{ color: colors.greenAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>

        {/* Wallet Stats */}
        <Box
          gridColumn="span 3"
          backgroundColor={colors.primary[400]}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="center"
          p="20px"
        >
          <AccountBalanceWalletIcon sx={{ color: colors.greenAccent[600], fontSize: "40px", mb: "10px" }} />
          <Typography variant="h6" color={colors.grey[100]} mb="10px">
            Total Wallet Activity
          </Typography>
          <Box display="flex" justifyContent="space-between" width="100%">
            <Typography variant="body1" color="black">
              Added: {walletStats.total.add}
            </Typography>
            <Typography variant="body1" color="black">
              Debited: {walletStats.total.debit}
            </Typography>
          </Box>
        </Box>

        {/* Monthly Added / Debited Card */}
        <Box
          gridColumn="span 3"
          backgroundColor={colors.primary[400]}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="center"
          p="20px"
        >
          <EventNoteIcon sx={{ color: colors.greenAccent[600], fontSize: "40px", mb: "10px" }} />
          <Typography variant="h6" color={colors.grey[100]} mb="10px">
            Monthly Wallet Activity
          </Typography>
          <Box display="flex" justifyContent="space-between" width="100%">
            <Typography variant="body1" color="black">
              Added: {walletStats.lastMonth.add}
            </Typography>
            <Typography variant="body1" color="black">
              Debited: {walletStats.lastMonth.debit}
            </Typography>
          </Box>
        </Box>

        {/* Weekly Added / Debited Card */}
        <Box
          gridColumn="span 3"
          backgroundColor={colors.primary[400]}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="center"
          p="20px"
        >
          <CalendarTodayIcon sx={{ color: colors.greenAccent[600], fontSize: "40px", mb: "10px" }} />
          <Typography variant="h6" color={colors.grey[100]} mb="10px">
            Weekly Wallet Activity
          </Typography>
          <Box display="flex" justifyContent="space-between" width="100%">
            <Typography variant="body1" color="black">
              Added: {walletStats.lastWeek.add}
            </Typography>
            <Typography variant="body1" color="black">
              Debited: {walletStats.lastWeek.debit}
            </Typography>
          </Box>
        </Box>

        {/* Today's Added / Debited Card */}
        <Box
          gridColumn="span 3"
          backgroundColor={colors.primary[400]}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="center"
          p="20px"
        >
          <AccessTimeIcon sx={{ color: colors.greenAccent[600], fontSize: "40px", mb: "10px" }} />
          <Typography variant="h6" color={colors.grey[100]} mb="10px">
            Today's Wallet Activity
          </Typography>
          <Box display="flex" justifyContent="space-between" width="100%">
            <Typography variant="body1" color="black">
              Added: {walletStats.today.add}
            </Typography>
            <Typography variant="body1" color="black">
              Debited: {walletStats.today.debit}
            </Typography>
          </Box>
        </Box>

      </Box>
    </Box>
  );
};

export default Dashboard;
